import dom from 'embo/utils/dom'
import {stop, on, off, getKey, getModifierState} from 'embo/utils/events'
import animate from 'embo/utils/animate'
import {initHeaderWave} from './header-wave'


const BREAKPOINT = 768 - 1

/** @const {MediaQueryList} */
const mediaQuery = window.matchMedia(`(max-width: ${BREAKPOINT}px)`)

const ACTIVE_NAV_WAVE_OPTIONS = {
  viewBoxHeight: 40,
  baseAmplitude: 4,
  hoverAmplitude: 8,
  kickAmplitude: 4,
  minWavelengths: 2,
  maxWavelengths: 4,
  toneEnabled: false,
}


function setScrollLocked(locked) {
  document.documentElement.classList.toggle('no-scroll', locked)
}

/**
 * Swaps the toggler/close buttons in place: they share the same slot in
 * #main-header-top, so only one of them is ever visible at a time.
 */
function syncMenuButtons(isOpen) {
  const toggler = dom.id('navbar-toggler')
  const close = dom.id('navbar-close')
  toggler.hidden = isOpen
  close.hidden = !isOpen
}


let activeNavWave = null

/**
 * (Re)starts the header-wave animation in the background of the nav-item
 * whose link currently carries the `.active` class, tearing down any
 * previous instance first.
 *
 * Called on init, and after every client-side navigation once the active
 * link has been recomputed (see updateHeaderNav() in ./routing/index.js).
 */
export function refreshActiveNavWave() {
  const nav = dom.id('main-header-navbar')
  if (!nav) return

  if (activeNavWave) {
    activeNavWave.destroy()
    activeNavWave = null
  }

  const activeLink = dom.qs('.nav-link.active', nav)
  const container = activeLink && dom.qs('.nav-item__wave', activeLink.parentElement)
  if (container) {
    activeNavWave = initHeaderWave(container, ACTIVE_NAV_WAVE_OPTIONS)
  }
}


export default function initializeNavbar() {
  const toggler = dom.id('navbar-toggler')
  const close = dom.id('navbar-close')
  const nav = dom.id('main-header-navbar')

  // hide side-panel by default
  // nav.classList.add('offscreen')
  dom.attrs(toggler, {
    role: 'button',
    'aria-controls': 'main-header-navbar',
    'aria-expanded': 'false',
  })

  on(toggler, 'click', e => {
    e.preventDefault()
    const wasHidden = nav.classList.contains('offscreen')
    nav.classList.toggle('offscreen')
    toggler.setAttribute('aria-expanded', wasHidden)
    setScrollLocked(wasHidden)
    syncMenuButtons(wasHidden)
  })

  on(close, 'click', e => {
    e.preventDefault()
    nav.classList.add('offscreen')
    const hidden = nav.classList.contains('offscreen')
    close.setAttribute('aria-expanded', hidden)
    setScrollLocked(false)
    syncMenuButtons(false)
  })

  //
  // Handle Keyboard focus ring
  //------------------------------------------------------------------------------------------------------------------

  const links = dom.qsa('a:not(.dropdown-toggle)', nav)
  const firstLink = links[0]
  const lastLink = links[links.length - 1]

  const onLastLinkKeydown = event => {
    // TAB at end of navigation block: return focus to navigation menu button
    if (getKey(event) === 'Tab' && !getModifierState(event, 'Shift')) {
      event.preventDefault()
      toggler.focus()
    }
  }
  const onFirstLinkKeydown = event => {
    // SHIFT+TAB at start of navigation block: refocus close button
    if (getKey(event) === 'Tab' && getModifierState(event, 'Shift')) {
      event.preventDefault()
      toggler.focus()
    }
  }
  const onTogglerKeydown = event => {
    if (getKey(event) !== 'Tab') {
      return
    }
    // If the menu is visible, always TAB into it from the menu button
    if (toggler.getAttribute('aria-expanded') === 'false') {
      return
    }
    if (!getModifierState(event, 'Shift')) {
      event.preventDefault()
      firstLink.focus()
      //console.log(links[0]);
    } else {
      // Do nothing since the link before toggler is an anchor to #main
      // otherwise we would have to dom.id('#main').focus();
    }
  }

  const trapFocus = () => {
    on(lastLink, 'keydown', onLastLinkKeydown)
    on(firstLink, 'keydown', onFirstLinkKeydown)
    on(toggler, 'keydown', onTogglerKeydown)
  }
  const releaseFocus = () => {
    off(lastLink, 'keydown', onLastLinkKeydown)
    off(firstLink, 'keydown', onFirstLinkKeydown)
    off(toggler, 'keydown', onTogglerKeydown)
  }

  const onMediaQueryChanged = mql => {

    if (mql.matches) {
      // Écran inférieur au breakpoint : menu fermé par défaut
      nav.classList.add('offscreen')
      setScrollLocked(false)
      syncMenuButtons(false)

      trapFocus()
    } else {
      // Écran supérieur ou égal au breakpoint : menu visible
      nav.classList.remove('offscreen')
      setScrollLocked(false)
      syncMenuButtons(false)

      releaseFocus()
    }
  }

  mediaQuery.addEventListener('change', onMediaQueryChanged)
  onMediaQueryChanged(mediaQuery)

  refreshActiveNavWave()
}

/**
 * Closes the mobile side-panel menu, if open.
 * Meant to be called once page navigation has completed (see ./routing/index.js),
 * rather than on link click, so the menu stays open while the next page loads.
 *
 * No-op above the mobile breakpoint: on desktop the navbar is an inline bar,
 * not an overlay, and `.offscreen` there would just hide it (see &.offscreen
 * in overrides/layout/_header.scss, which only resets `transform` — not
 * `display` — above the breakpoint).
 */
export function closeNavbar() {
  if (!mediaQuery.matches) return

  const toggler = dom.id('navbar-toggler')
  const nav = dom.id('main-header-navbar')

  if (!nav || nav.classList.contains('offscreen')) return

  nav.classList.add('offscreen')
  toggler.setAttribute('aria-expanded', 'false')
  setScrollLocked(false)
  syncMenuButtons(false)
}
